import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";
import {
	defaultKSK1120_R01DetailDto,
	defaultKSK1120_R01HeaderDto,
	KSK1120_R01DetailDto,
	KSK1120_R01HeaderDto,
} from "./Dto_KSK1120_R01";

export interface KSK1120_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	customCd: string;
	customNm: string;
	itemCd: string;
	itemNm: string;
	includeCompleted: boolean;
}

export interface Store_KSK1120_R01 {
	KSK1120_R01Filter: KSK1120_R01Filter;
	KSK1120_R01PastFilter: KSK1120_R01Filter;
	setKSK1120_R01Filter: (filter: Partial<KSK1120_R01Filter>) => void;
	setKSK1120_R01PastFilter: (filter: Partial<KSK1120_R01Filter>) => void;

	KSK1120_R01List: KSK1120_R01HeaderDto[];
	setKSK1120_R01List: (list: KSK1120_R01HeaderDto[]) => void;
	selectedKSK1120_R01: KSK1120_R01HeaderDto | undefined;
	setSelectedKSK1120_R01: (data: KSK1120_R01HeaderDto | undefined) => void;
	initSelectedKSK1120_R01: () => void;

	editKSK1120_R01: KSK1120_R01DetailDto;
	setEditKSK1120_R01: (data: KSK1120_R01DetailDto) => void;

	KSK1120_R01DetailList: KSK1120_R01DetailDto[];
	setKSK1120_R01DetailList: (list: KSK1120_R01DetailDto[]) => void;
	updateKSK1120_R01DetailList: (valueIndex: number, data: KSK1120_R01DetailDto) => void;
	originalKSK1120_R01DetailList: KSK1120_R01DetailDto[];
	setOriginalKSK1120_R01DetailList: (list: KSK1120_R01DetailDto[]) => void;

	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;
	initData: () => void;
}

const createDefaultKSK1120_R01Filter = (): KSK1120_R01Filter => ({
	compCd: "",
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	customCd: "",
	customNm: "",
	itemCd: "",
	itemNm: "",
	includeCompleted: true,
});

export const defaultKSK1120_R01Filter: KSK1120_R01Filter = createDefaultKSK1120_R01Filter();

const createIndexedHeaderList = (list: KSK1120_R01HeaderDto[]): KSK1120_R01HeaderDto[] =>
	list.map((item, index) => ({
		...defaultKSK1120_R01HeaderDto,
		...item,
		valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
		orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
		orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
	}));

const createIndexedDetailList = (list: KSK1120_R01DetailDto[]): KSK1120_R01DetailDto[] =>
	list.map((item, index) => ({
		...defaultKSK1120_R01DetailDto,
		...item,
		valueIndex: item.valueIndex > 0 ? item.valueIndex : index + 1,
		orderIndex: item.orderIndex > 0 ? item.orderIndex : index + 1,
		orderIndexSeq: item.orderIndexSeq > 0 ? item.orderIndexSeq : 0,
	}));

export const useKSK1120_R01Store = create<Store_KSK1120_R01>((set) => ({
	KSK1120_R01Filter: createDefaultKSK1120_R01Filter(),
	KSK1120_R01PastFilter: createDefaultKSK1120_R01Filter(),
	setKSK1120_R01Filter: (filter) => set((state) => ({
		KSK1120_R01Filter: { ...state.KSK1120_R01Filter, ...filter },
	})),
	setKSK1120_R01PastFilter: (filter) => set((state) => ({
		KSK1120_R01PastFilter: { ...state.KSK1120_R01PastFilter, ...filter },
	})),

	KSK1120_R01List: [],
	setKSK1120_R01List: (list) => set({ KSK1120_R01List: createIndexedHeaderList(list) }),
	selectedKSK1120_R01: undefined,
	setSelectedKSK1120_R01: (data) => set({ selectedKSK1120_R01: data }),
	initSelectedKSK1120_R01: () => set({ selectedKSK1120_R01: undefined }),

	editKSK1120_R01: { ...defaultKSK1120_R01DetailDto },
	setEditKSK1120_R01: (data) => set({ editKSK1120_R01: data }),

	KSK1120_R01DetailList: [],
	setKSK1120_R01DetailList: (list) => set({ KSK1120_R01DetailList: createIndexedDetailList(list) }),
	updateKSK1120_R01DetailList: (valueIndex, data) => set((state) => ({
		KSK1120_R01DetailList: state.KSK1120_R01DetailList.map((item) =>
			item.valueIndex === valueIndex ? data : item),
	})),
	originalKSK1120_R01DetailList: [],
	setOriginalKSK1120_R01DetailList: (list) => set({
		originalKSK1120_R01DetailList: createIndexedDetailList(list).map((item) => ({ ...item })),
	}),

	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),
	initData: () => set({
		KSK1120_R01Filter: createDefaultKSK1120_R01Filter(),
		KSK1120_R01PastFilter: createDefaultKSK1120_R01Filter(),
		KSK1120_R01List: [],
		selectedKSK1120_R01: undefined,
		editKSK1120_R01: { ...defaultKSK1120_R01DetailDto },
		KSK1120_R01DetailList: [],
		originalKSK1120_R01DetailList: [],
		columnState: null,
		detailColumnState: null,
		scrollTop: null,
		detailScrollTop: null,
		allCount: 0,
	}),
}));

